import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  CalendarDays, CalendarClock, Flag, ClipboardList, PackageOpen,
  MessageSquareText, Award, ChevronRight,
} from 'lucide-react';

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Hero = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-left: 4px solid ${({ theme, $critical }) =>
    ($critical ? theme.colors.auxiliar.danger : theme.colors.primary.main)};
  border-radius: 12px;
  padding: 20px 22px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const HeroTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const ExitTypeLabel = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const UrgencyBadge = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $critical }) =>
    ($critical ? 'rgba(229,57,53,0.10)' : 'rgba(2,145,239,0.10)')};
  color: ${({ theme, $critical }) =>
    ($critical ? theme.colors.auxiliar.danger : theme.colors.primary.main)};
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Countdown = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const CountNumber = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 40px;
  line-height: 1;
  font-weight: ${({ theme }) => theme.metrics.font.weight.extraBold};
  color: ${({ theme, $critical }) =>
    ($critical ? theme.colors.auxiliar.danger : theme.colors.primary.main)};
`;

const CountLabel = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const ProgressWrap = styled.div`
  margin-top: 18px;
`;

const ProgressHead = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 6px;
`;

const ProgressTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.bgColor};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => `${$pct}%`};
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.secondary.main};
  transition: width 0.3s ease;
`;

const MetaRow = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
`;

const StatusCard = styled.button`
  text-align: left;
  background: #fff;
  border: 1px solid ${({ theme, $highlight }) =>
    ($highlight ? theme.colors.auxiliar.warning : '#E8ECF0')};
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: box-shadow 0.15s, transform 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
`;

const CardIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardMeta = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 2px;
`;

const AlumniBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  background: rgba(2, 211, 191, 0.12);
  color: ${({ theme }) => theme.colors.secondary.dark};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  padding: 8px 14px;
  border-radius: 999px;
`;

const fmtDate = (iso, lang) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(lang || undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export default function OverviewTab({ home, onGoTab }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const critical = home.urgency === 'critical_immediate' || home.exitType === 'termination';
  const days = home.daysUntilLastDay;

  const handoverHighlight = home.handoverStatus === 'draft' || home.handoverStatus === 'rejected';
  const interviewHighlight = home.exitInterviewStatus === 'sent' || home.exitInterviewStatus === 'pending';

  return (
    <Grid>
      <Hero $critical={critical}>
        <HeroTop>
          <ExitTypeLabel>
            {t(`offboarding.exitType.${home.exitType}`, { defaultValue: home.exitType })}
          </ExitTypeLabel>
          <UrgencyBadge $critical={critical}>
            <Flag size={13} />
            {t(`offboarding.urgency.${home.urgency}`, { defaultValue: home.urgency })}
          </UrgencyBadge>
        </HeroTop>

        <Countdown>
          {days > 0 ? (
            <>
              <CountNumber $critical={critical}>{days}</CountNumber>
              <CountLabel>{t('myOffboarding.overview.daysUntilLastDay')}</CountLabel>
            </>
          ) : (
            <CountLabel>{t('myOffboarding.overview.lastDayPassed')}</CountLabel>
          )}
        </Countdown>

        <ProgressWrap>
          <ProgressHead>
            <span>{t('myOffboarding.overview.progress')}</span>
            <span>%{home.overallProgressPct}</span>
          </ProgressHead>
          <ProgressTrack>
            <ProgressFill $pct={home.overallProgressPct} />
          </ProgressTrack>
        </ProgressWrap>

        <MetaRow>
          <MetaItem>
            <CalendarClock size={16} />
            {t('myOffboarding.overview.exitDate')}:&nbsp;<strong>{fmtDate(home.exitDate, lang)}</strong>
          </MetaItem>
          <MetaItem>
            <CalendarDays size={16} />
            {t('myOffboarding.overview.lastWorkDay')}:&nbsp;<strong>{fmtDate(home.lastWorkDay, lang)}</strong>
          </MetaItem>
          <MetaItem>
            <Flag size={16} />
            {t('myOffboarding.overview.initiatedAt')}:&nbsp;<strong>{fmtDate(home.initiatedAt, lang)}</strong>
          </MetaItem>
        </MetaRow>
      </Hero>

      <CardsGrid>
        <StatusCard onClick={() => onGoTab('tasks')}>
          <CardIcon $bg="rgba(2,145,239,0.10)" $color="#0291EF">
            <ClipboardList size={20} />
          </CardIcon>
          <CardBody>
            <CardTitle>{t('myOffboarding.tab.tasks')}</CardTitle>
            <CardMeta>
              {t('myOffboarding.overview.mandatoryTasks', {
                done: home.mandatoryTaskCompleted,
                total: home.mandatoryTaskTotal,
              })}
            </CardMeta>
          </CardBody>
          <ChevronRight size={18} color="#A0A8B4" />
        </StatusCard>

        <StatusCard $highlight={handoverHighlight} onClick={() => onGoTab('handover')}>
          <CardIcon $bg="rgba(2,211,191,0.12)" $color="#01A899">
            <PackageOpen size={20} />
          </CardIcon>
          <CardBody>
            <CardTitle>{t('myOffboarding.tab.handover')}</CardTitle>
            <CardMeta>
              {t(`myOffboarding.handover.status.${home.handoverStatus}`, { defaultValue: home.handoverStatus })}
            </CardMeta>
          </CardBody>
          <ChevronRight size={18} color="#A0A8B4" />
        </StatusCard>

        <StatusCard $highlight={interviewHighlight} onClick={() => onGoTab('interview')}>
          <CardIcon $bg="rgba(255,193,7,0.14)" $color="#C79100">
            <MessageSquareText size={20} />
          </CardIcon>
          <CardBody>
            <CardTitle>{t('myOffboarding.tab.interview')}</CardTitle>
            <CardMeta>
              {t(`myOffboarding.interview.status.${home.exitInterviewStatus}`, { defaultValue: home.exitInterviewStatus })}
            </CardMeta>
          </CardBody>
          <ChevronRight size={18} color="#A0A8B4" />
        </StatusCard>
      </CardsGrid>

      {home.alumniConsentObtained && (
        <AlumniBadge>
          <Award size={16} />
          {t('myOffboarding.overview.alumniConsent')}
        </AlumniBadge>
      )}
    </Grid>
  );
}
